<template>
  <div class="location-tracker">
    <InfoIcon
      v-if="hasError"
      class="text-danger"
      icon="pi pi-exclamation-triangle"
      :tooltip="t('locations.errors.mileage')"
    />

    <div v-else class="location-tracker-distance">
      {{
        mileage !== undefined
          ? t('common.paramMilesAbbr', { param: Math.round(mileage) })
          : ''
      }}
    </div>

    <div
      class="flex flex-column flex-gap-1 justify-content-start align-items-center h-full"
    >
      <svg
        version="1.1"
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        class="location-tracker-icon"
        :class="{
          pickup: stopType === ShipmentStopType.PICKUP,
          dropoff: stopType === ShipmentStopType.DROP_OFF,
        }"
      >
        <circle cx="50" cy="50" r="48" />

        <text v-if="sequenceNumber > 0" x="50" y="50" class="sequence-number">
          {{ sequenceNumber }}
        </text>
      </svg>

      <div
        class="location-tracker-vertical-line"
        :class="{ 'opacity-0': !hasNextStop }"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { ShipmentStopType } from '@/model';

import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';

const props = defineProps({
  mileage: {
    type: Number,
    default: undefined,
  },
  stopType: {
    type: String as PropType<ShipmentStopType>,
    default: () => ShipmentStopType.PICKUP,
  },
  sequenceNumber: {
    type: Number,
    default: () => -1,
  },
  hasNextStop: {
    type: Boolean,
    default: () => false,
  },
  hasError: {
    type: Boolean,
    default: () => false,
  },
});
const { mileage, stopType, sequenceNumber, hasNextStop, hasError } =
  toRefs(props);

const { t } = useI18n();
</script>

<style>
:root {
  --location-tracker-icon-margin-top: 0;
}

.location-tracker {
  display: flex;
  align-items: center;
  gap: var(--location-tracker-gap);
  width: var(--location-tracker-width);

  .info-icon-container {
    color: var(--danger-color);
    width: var(--location-tracker-font-size);
  }

  .location-tracker-distance {
    color: var(--p-gray-400);
    writing-mode: vertical-rl;
    rotate: 180deg;
    font-size: var(--location-tracker-font-size);
    width: var(--location-tracker-font-size);
  }

  svg.location-tracker-icon {
    flex: 1 0 var(--input-height);
    margin-top: var(--location-tracker-icon-margin-top);

    circle {
      fill: var(--stop-type-accent-color, var(--p-gray-400));
    }

    .sequence-number {
      font-size: 48px;
      font-weight: bold;
      dominant-baseline: central;
      text-anchor: middle;
      fill: white;
    }
  }

  .location-tracker-vertical-line {
    width: var(--sp-1);
    height: 100%;
    background-color: var(--p-gray-300);
  }
}
</style>
